import { IPlanData, IPurchaseOptions } from "@src/types";

import { POPULAR_PLAN } from "@/constants";

const DAYS_PER_MONTH = 30;
const DAYS_PER_QUARTER = 90;
const DAYS_PER_YEAR = 365;
const MAX_SAVINGS_PERCENTAGE = 100;

export type BillingCycleId = "monthly" | "quarterly" | "annually";

export type DurationPlanView = {
  id: BillingCycleId;
  planId: string;
  total: number;
  perDay: number;
  currency: string;
  save?: number;
};

function selectPreferredPlan(plans: IPlanData[] | undefined): IPlanData | undefined {
  if (!plans?.length) {
    return undefined;
  }
  return plans.find((plan) => plan.role === POPULAR_PLAN) ?? plans[0];
}

function calculatePerDayPrice(plan: IPlanData | undefined, daysInPeriod: number): number {
  return plan ? plan.price / daysInPeriod : 0;
}

function calculateSavingsPercentage(basePerDay: number, discountedPerDay: number): number {
  if (basePerDay <= 0 || discountedPerDay <= 0) {
    return 0;
  }
  const percentage = Math.round(((basePerDay - discountedPerDay) / basePerDay) * MAX_SAVINGS_PERCENTAGE);
  return percentage > 0 ? percentage : 0;
}

export default function buildDurationPlans(purchaseOptions: IPurchaseOptions): DurationPlanView[] {
  const monthlyPlan = selectPreferredPlan(purchaseOptions.monthly_plans);
  const quarterlyPlan = selectPreferredPlan(purchaseOptions.quarterly_plans);
  const annualPlan = selectPreferredPlan(purchaseOptions.annual_plans);

  const monthlyPerDay = calculatePerDayPrice(monthlyPlan, DAYS_PER_MONTH);
  const quarterlyPerDay = calculatePerDayPrice(quarterlyPlan, DAYS_PER_QUARTER);
  const annualPerDay = calculatePerDayPrice(annualPlan, DAYS_PER_YEAR);

  const plans: DurationPlanView[] = [];

  if (monthlyPlan) {
    plans.push({
      id: "monthly",
      planId: monthlyPlan.plan_id,
      total: monthlyPlan.price,
      perDay: monthlyPerDay,
      currency: monthlyPlan.currency,
    });
  }

  if (quarterlyPlan) {
    plans.push({
      id: "quarterly",
      planId: quarterlyPlan.plan_id,
      total: quarterlyPlan.price,
      perDay: quarterlyPerDay,
      currency: quarterlyPlan.currency,
      save: calculateSavingsPercentage(monthlyPerDay, quarterlyPerDay) || undefined,
    });
  }

  if (annualPlan) {
    plans.push({
      id: "annually",
      planId: annualPlan.plan_id,
      total: annualPlan.price,
      perDay: annualPerDay,
      currency: annualPlan.currency,
      save: calculateSavingsPercentage(monthlyPerDay, annualPerDay) || undefined,
    });
  }

  return plans;
}
